import { OVERLAY_OPEN, OVERLAY_CLOSE } from './arctic-header-events.js';

const SEARCH_DEBOUNCE_MS = 300;

class ArcticHeaderSearch extends HTMLElement {
  constructor() {
    super();
    this._activeFetch = null;
    this._searchDebounceId = null;
    this._suppressOpenUntil = 0;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-header-search-form]');
    this.input = this.querySelector('[data-header-search-input]');
    this.closeButton = this.querySelector('[data-header-search-close]');
    this.panel = this.querySelector('[data-header-search-panel]');
    this.results = this.querySelector('[data-header-search-results]');
    this.emptyState = this.querySelector('[data-header-search-empty]');

    if (!this.form || !this.input || !this.panel || !this.results) return;

    this._emptyStateHTML = this.emptyState?.outerHTML ?? '';
    this._setCloseButtonVisible(false);

    this._onFocus = this._handleFocus.bind(this);
    this._onInput = this._handleInput.bind(this);
    this._onCloseClick = this._handleCloseClick.bind(this);
    this._onDocumentPointerDown = this._handleDocumentPointerDown.bind(this);
    this._onDocumentKeydown = this._handleDocumentKeydown.bind(this);

    this.input.addEventListener('focus', this._onFocus);
    this.input.addEventListener('input', this._onInput);
    this.closeButton?.addEventListener('click', this._onCloseClick);
    document.addEventListener('pointerdown', this._onDocumentPointerDown, true);
    document.addEventListener('keydown', this._onDocumentKeydown);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('focus', this._onFocus);
    this.input?.removeEventListener('input', this._onInput);
    this.closeButton?.removeEventListener('click', this._onCloseClick);
    document.removeEventListener('pointerdown', this._onDocumentPointerDown, true);
    document.removeEventListener('keydown', this._onDocumentKeydown);
    this._cancelInFlightFetch();
    this._clearDebounce();
    this._setBodyOpen(false);
  }

  open() {
    if (!this.panel || Date.now() < this._suppressOpenUntil) return;

    document.querySelectorAll('arctic-header-search.is-open').forEach((search) => {
      if (search !== this && typeof search.close === 'function') {
        search.close({ blur: false });
      }
    });

    if (this.classList.contains('is-open')) return;

    this.classList.add('is-open');
    this.panel.hidden = false;
    this._setCloseButtonVisible(true);
    this.input.setAttribute('aria-expanded', 'true');
    this._setBodyOpen(true);
    this._dispatchOverlayEvent(OVERLAY_OPEN);

    if (this.query.length > 0) {
      this._fetchPredictiveSearch(this.query);
    } else {
      this._showEmptyState();
    }
  }

  close({ blur = true } = {}) {
    if (!this.classList.contains('is-open')) return;

    this._suppressOpenUntil = Date.now() + 450;
    this.classList.remove('is-open', 'is-loading', 'has-results');
    this.panel.hidden = true;
    this._setCloseButtonVisible(false);
    this.input.setAttribute('aria-expanded', 'false');
    this._cancelInFlightFetch();
    this._clearDebounce();
    this._setBodyOpen(false);
    this._dispatchOverlayEvent(OVERLAY_CLOSE);

    if (blur) this.input.blur();
  }

  get query() {
    return this.input.value.trim();
  }

  _handleFocus() {
    this.open();
  }

  _handleInput() {
    this.open();
    this._clearDebounce();

    if (this.query.length === 0) {
      this._cancelInFlightFetch();
      this.classList.remove('has-results');
      this._showEmptyState();
      return;
    }

    this._searchDebounceId = setTimeout(() => {
      this._fetchPredictiveSearch(this.query);
    }, SEARCH_DEBOUNCE_MS);
  }

  _handleCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  _handleDocumentPointerDown(event) {
    if (!this.classList.contains('is-open')) return;
    if (this.contains(event.target)) return;
    this.close();
  }

  _handleDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
    event.preventDefault();
    this.close();
  }

  _showEmptyState() {
    if (!this._emptyStateHTML) return;
    this.results.innerHTML = this._emptyStateHTML;
    window.dispatchEvent(
      new CustomEvent('northbound:surfaces-changed', {
        detail: { source: 'header-search-empty' },
      })
    );
  }

  async _fetchPredictiveSearch(term) {
    this._cancelInFlightFetch();
    const controller = new AbortController();
    this._activeFetch = controller;
    this.classList.add('is-loading');

    const url = new URL(
      (window.routes && window.routes.predictive_search_url) || '/search/suggest',
      window.location.origin
    );
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');

    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const text = await response.text();
      if (controller.signal.aborted) return;

      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const sectionRoot = parsed.querySelector('#shopify-section-predictive-search');
      this.results.innerHTML = sectionRoot ? sectionRoot.innerHTML : text;
      window.dispatchEvent(
        new CustomEvent('northbound:surfaces-changed', {
          detail: { source: 'predictive-search' },
        })
      );
      this.classList.add('has-results');
    } catch (error) {
      if (error.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn('[arctic-header-search]', error);
      }
    } finally {
      if (this._activeFetch === controller) {
        this._activeFetch = null;
        this.classList.remove('is-loading');
      }
    }
  }

  _clearDebounce() {
    if (!this._searchDebounceId) return;
    clearTimeout(this._searchDebounceId);
    this._searchDebounceId = null;
  }

  _cancelInFlightFetch() {
    if (!this._activeFetch) return;
    this._activeFetch.abort();
    this._activeFetch = null;
    this.classList.remove('is-loading');
  }

  _setBodyOpen(isOpen) {
    document.body.classList.toggle('search-open', isOpen);
  }

  _setCloseButtonVisible(isVisible) {
    if (!this.closeButton) return;
    this.closeButton.hidden = false;
    this.closeButton.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    this.closeButton.tabIndex = isVisible ? 0 : -1;
  }

  _dispatchOverlayEvent(name) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: { el: this.panel, source: 'header-search' },
      })
    );
  }
}

if (!customElements.get('arctic-header-search')) {
  customElements.define('arctic-header-search', ArcticHeaderSearch);
}
